import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Link2, RefreshCw, Search, CheckCircle, AlertTriangle } from "lucide-react";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";

interface UnlinkedBusiness {
    business_id: number;
    business_name: string | null;
    senta_company_id: number;
    senta_id: string;
    senta_title: string | null;
    client_ref: string | null;
    client_state: string | null;
    primary_contact_email: string | null;
}

interface Candidate {
    id: number;
    name: string | null;
    email: string | null;
    client_ref: string | null;
    contact_active: boolean;
    existing_senta_company_id: number | null;
    existing_business_id: number | null;
    existing_business_name?: string | null;
    existing_senta_title?: string | null;
    existing_client_ref?: string | null;
    name_similarity: number;
    client_ref_match: boolean;
    email_match?: boolean;
    email_domain_match?: boolean;
}

interface AutoSuggestion {
    business_id: number;
    business_name: string | null;
    senta_company_id: number;
    senta_id: string;
    senta_title: string | null;
    client_ref: string | null;
    client_state: string | null;
    primary_contact_email: string | null;
    candidate_id: number;
    candidate_name: string | null;
    candidate_email: string | null;
    candidate_client_ref: string | null;
    candidate_active: boolean;
    existing_senta_company_id: number | null;
    existing_business_id: number | null;
    existing_business_name?: string | null;
    existing_senta_title?: string | null;
    existing_client_ref?: string | null;
    name_similarity: number;
    client_ref_match: boolean;
    email_match: boolean;
    email_domain_match: boolean;
}

const CLIENT_STATE_LABEL: Record<string, string> = {
    "l-clientstate-c": "Client",
    "l-clientstate-p": "Prospect",
    "l-clientstate-a": "Archived",
};

const confidencePill = (
    sim: number,
    refMatch: boolean,
    emailMatch?: boolean,
    emailDomainMatch?: boolean
) => {
    if (emailMatch) return { label: "email match", color: "#2ea043" };
    if (refMatch) return { label: "client_ref match", color: "#2ea043" };
    if (emailDomainMatch && sim >= 0.45) return { label: "domain + name", color: "#2ea043" };
    if (emailDomainMatch) return { label: "email domain", color: "#d29922" };
    if (sim >= 0.7) return { label: "High", color: "#2ea043" };
    if (sim >= 0.45) return { label: "Medium", color: "#d29922" };
    if (sim >= 0.25) return { label: "Low", color: "#f85149" };
    return { label: "Very low", color: "#8b949e" };
};

const XeroContactLinker: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();
    const apiBaseUrl = getApiBaseUrl();

    const [businesses, setBusinesses] = useState<UnlinkedBusiness[]>([]);
    const [filter, setFilter] = useState("");
    const [stateFilter, setStateFilter] = useState<string>("l-clientstate-c");
    const [selected, setSelected] = useState<UnlinkedBusiness | null>(null);
    const [candidates, setCandidates] = useState<Candidate[]>([]);
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    const [linkingId, setLinkingId] = useState<number | null>(null);
    const [message, setMessage] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
    const [mode, setMode] = useState<"manual" | "auto">("manual");
    const [suggestions, setSuggestions] = useState<AutoSuggestion[]>([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [minSimilarity, setMinSimilarity] = useState<number>(0.45);
    const [suggestionsElapsedSec, setSuggestionsElapsedSec] = useState(0);
    const [suggestionsStatus, setSuggestionsStatus] = useState<string>("");
    const [cacheLastRefreshedAt, setCacheLastRefreshedAt] = useState<string | null>(null);
    const [cacheRows, setCacheRows] = useState<number>(0);
    const [jobStatus, setJobStatus] = useState<{ Enabled?: boolean; LastRunAt?: string; LastStatus?: string; LastError?: string } | null>(null);
    const autoSuggestAbortRef = useRef<AbortController | null>(null);

    const getToken = useCallback(async () => {
        if (!accounts[0]) throw new Error("Not signed in");
        const token = await acquireTokenSafe(instance, accounts);
        return token;
    }, [instance, accounts]);

    const fetchBusinesses = useCallback(async () => {
        setLoading(true);
        setMessage(null);
        try {
            const token = await getToken();
            const res = await fetch(`${apiBaseUrl}/api/xero-link/unlinked-businesses`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setBusinesses(data.businesses || []);
        } catch (e: any) {
            setMessage({ kind: "err", text: `Failed to load businesses: ${e.message}` });
        } finally {
            setLoading(false);
        }
    }, [apiBaseUrl, getToken, setLoading]);

    useEffect(() => {
        fetchBusinesses();
    }, [fetchBusinesses]);

    const fetchCandidates = useCallback(
        async (b: UnlinkedBusiness) => {
            setSelected(b);
            setCandidates([]);
            setLoadingCandidates(true);
            try {
                const token = await getToken();
                const res = await fetch(
                    `${apiBaseUrl}/api/xero-link/candidates?businessId=${b.business_id}&limit=10`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                setCandidates(data.candidates || []);
            } catch (e: any) {
                setMessage({ kind: "err", text: `Failed to load candidates: ${e.message}` });
            } finally {
                setLoadingCandidates(false);
            }
        },
        [apiBaseUrl, getToken]
    );

    const linkContact = useCallback(
        async (
            business: { business_id: number; senta_company_id: number; business_name: string | null; senta_title: string | null },
            xeroContactId: number
        ) => {
            const label = business.business_name || business.senta_title || `business #${business.business_id}`;

            setLinkingId(xeroContactId);
            setMessage(null);
            try {
                const token = await getToken();
                const res = await fetch(`${apiBaseUrl}/api/xero-link/assign`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        businessId: business.business_id,
                        xeroContactId,
                    }),
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
                setMessage({
                    kind: "ok",
                    text: `Linked Xero contact "${data.xeroContact?.name}" to ${label}.`,
                });
                // Drop the now-linked business from the list and clear selection
                setBusinesses((prev) =>
                    prev.filter((b) => b.business_id !== business.business_id)
                );
                setSuggestions((prev) =>
                    prev.filter((s) => s.business_id !== business.business_id)
                );
                if (selected?.business_id === business.business_id) {
                    setSelected(null);
                    setCandidates([]);
                }
            } catch (e: any) {
                setMessage({ kind: "err", text: `Link failed: ${e.message}` });
            } finally {
                setLinkingId(null);
            }
        },
        [apiBaseUrl, getToken, selected]
    );

    const fetchSuggestions = useCallback(
        async (threshold: number) => {
            setLoadingSuggestions(true);
            setMessage(null);
            setSuggestionsStatus("Preparing auto-suggestions...");
            setSuggestionsElapsedSec(0);

            if (autoSuggestAbortRef.current) {
                autoSuggestAbortRef.current.abort();
            }
            const controller = new AbortController();
            autoSuggestAbortRef.current = controller;
            const timeoutId = window.setTimeout(() => controller.abort(), 45000);

            try {
                const token = await getToken();
                setSuggestionsStatus("Querying unlinked businesses and scoring likely matches...");
                const res = await fetch(
                    `${apiBaseUrl}/api/xero-link/auto-suggestions?minSimilarity=${threshold}&maxSuggestions=500`,
                    { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
                );
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                setSuggestions(data.suggestions || []);
                setCacheLastRefreshedAt(data.lastRefreshedAt || null);
                setCacheRows(data.cachedRows || 0);
                setJobStatus(data.jobStatus || null);
                setSuggestionsStatus(`Completed: ${data.count || (data.suggestions || []).length} suggestions`);
            } catch (e: any) {
                if (e?.name === 'AbortError') {
                    setMessage({ kind: "err", text: "Auto-suggestions timed out after 45s. Try a higher similarity threshold (e.g. 0.6 or 0.7)." });
                    setSuggestionsStatus("Timed out");
                } else {
                    // Try to surface the underlying error from the server, then run diagnostics.
                    let detail = e?.message || "";
                    try {
                        const token = await getToken();
                        const diagRes = await fetch(`${apiBaseUrl}/api/xero-link/auto-suggestions/diagnose`, {
                            headers: { Authorization: `Bearer ${token}` },
                        });
                        if (diagRes.ok) {
                            const diag = await diagRes.json();
                            setJobStatus(diag.jobStatus || null);
                            setCacheRows(diag.cachedRows || 0);
                            setCacheLastRefreshedAt(diag.lastRefreshedAt || null);
                            const bits: string[] = [];
                            if (!diag.sqlPoolReady) bits.push("SQL Server not ready");
                            if (!diag.pgPoolReady) bits.push("Postgres not ready");
                            if (diag.ensureError) bits.push(`Cache table error: ${diag.ensureError}`);
                            if (diag.jobStatus?.LastStatus === 'Failed' && diag.jobStatus?.LastError) {
                                bits.push(`Scheduled job failed: ${diag.jobStatus.LastError}`);
                            }
                            if (bits.length) detail = `${detail} — ${bits.join('; ')}`;
                        }
                    } catch {/* ignore diagnostic errors */}
                    setMessage({ kind: "err", text: `Failed to load suggestions: ${detail}` });
                    setSuggestionsStatus("Failed");
                }
            } finally {
                window.clearTimeout(timeoutId);
                if (autoSuggestAbortRef.current === controller) {
                    autoSuggestAbortRef.current = null;
                }
                setLoadingSuggestions(false);
            }
        },
        [apiBaseUrl, getToken]
    );

    useEffect(() => {
        if (!loadingSuggestions) return;
        const timer = window.setInterval(() => {
            setSuggestionsElapsedSec((s) => s + 1);
        }, 1000);
        return () => window.clearInterval(timer);
    }, [loadingSuggestions]);

    useEffect(() => {
        return () => {
            if (autoSuggestAbortRef.current) {
                autoSuggestAbortRef.current.abort();
            }
        };
    }, []);

    useEffect(() => {
        if (mode === "auto") {
            fetchSuggestions(minSimilarity);
        }
    }, [mode, minSimilarity, fetchSuggestions]);

    const stateCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        for (const b of businesses) {
            const k = b.client_state || "__none__";
            counts[k] = (counts[k] || 0) + 1;
        }
        return counts;
    }, [businesses]);

    const filtered = useMemo(() => {
        let rows = businesses;
        if (stateFilter !== "__all__") {
            if (stateFilter === "__none__") {
                rows = rows.filter((b) => !b.client_state);
            } else {
                rows = rows.filter((b) => b.client_state === stateFilter);
            }
        }
        if (filter.trim()) {
            const q = filter.toLowerCase();
            rows = rows.filter((b) =>
                [b.business_name, b.senta_title, b.client_ref, b.senta_id, b.primary_contact_email]
                    .filter(Boolean)
                    .some((v) => (v as string).toLowerCase().includes(q))
            );
        }
        return rows;
    }, [businesses, filter, stateFilter]);

    const columns: TableColumn<UnlinkedBusiness>[] = [
        {
            name: "Business",
            selector: (r) => r.business_name || r.senta_title || "(unnamed)",
            sortable: true,
            grow: 2,
            cell: (r) => (
                <div>
                    <div style={{ fontWeight: 500 }}>
                        {r.business_name || r.senta_title || "(unnamed)"}
                    </div>
                    {r.business_name && r.senta_title && r.business_name !== r.senta_title && (
                        <div style={{ fontSize: 11, color: "#8b949e" }}>
                            Senta: {r.senta_title}
                        </div>
                    )}
                    {r.primary_contact_email && (
                        <div style={{ fontSize: 11, color: "#58a6ff" }}>
                            {r.primary_contact_email}
                        </div>
                    )}
                </div>
            ),
        },
        {
            name: "Client Ref",
            selector: (r) => r.client_ref || "",
            sortable: true,
            width: "120px",
        },
        {
            name: "State",
            selector: (r) => r.client_state || "",
            width: "120px",
            cell: (r) =>
                r.client_state ? CLIENT_STATE_LABEL[r.client_state] || r.client_state : "—",
        },
        {
            name: "Senta ID",
            selector: (r) => r.senta_id,
            width: "150px",
            cell: (r) => <code style={{ fontSize: 11 }}>{r.senta_id}</code>,
        },
        {
            name: "",
            width: "140px",
            cell: (r) => (
                <button
                    className="btn btn-sm btn-primary"
                    onClick={() => fetchCandidates(r)}
                    style={{
                        padding: "4px 10px",
                        fontSize: 12,
                        background: selected?.business_id === r.business_id ? "#1f6feb" : "#238636",
                        color: "#fff",
                        border: "none",
                        borderRadius: 4,
                        cursor: "pointer",
                    }}
                >
                    <Search size={12} style={{ marginRight: 4 }} />
                    Find matches
                </button>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <h2 style={{ display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
                    <Link2 size={22} /> Xero Contact Linker
                </h2>
                <button
                    onClick={fetchBusinesses}
                    style={{
                        background: "#21262d",
                        color: "#c9d1d9",
                        border: "1px solid #30363d",
                        borderRadius: 4,
                        padding: "6px 12px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                    }}
                >
                    <RefreshCw size={14} /> Refresh
                </button>
            </div>

            <p style={{ color: "#8b949e", marginTop: 0 }}>
                Lists businesses that have a Senta company but no linked Xero contact. Click <em>Find matches</em> to see fuzzy-name candidates from the existing Xero contact pool. Linking sets <code>xero_contact.senta_company_id</code> and <code>xero_contact.business_id</code> in a single transaction (mirrors MKP).
            </p>

            {message && (
                <div
                    style={{
                        padding: "8px 12px",
                        borderRadius: 4,
                        marginBottom: 12,
                        background: message.kind === "ok" ? "#0f5132" : "#842029",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                    }}
                >
                    {message.kind === "ok" ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                    {message.text}
                </div>
            )}

            <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ display: "inline-flex", border: "1px solid #30363d", borderRadius: 6, overflow: "hidden" }}>
                    <button
                        onClick={() => setMode("manual")}
                        style={{
                            background: mode === "manual" ? "#1f6feb" : "#0d1117",
                            color: "#c9d1d9",
                            border: "none",
                            padding: "6px 14px",
                            fontSize: 13,
                            cursor: "pointer",
                        }}
                    >
                        Manual review
                    </button>
                    <button
                        onClick={() => setMode("auto")}
                        style={{
                            background: mode === "auto" ? "#1f6feb" : "#0d1117",
                            color: "#c9d1d9",
                            border: "none",
                            padding: "6px 14px",
                            fontSize: 13,
                            cursor: "pointer",
                        }}
                    >
                        Auto-suggest medium+ matches
                    </button>
                </div>
                {mode === "auto" && (
                    <>
                        <label style={{ fontSize: 12, color: "#8b949e", marginLeft: 8 }}>
                            Min name similarity:
                        </label>
                        <select
                            value={minSimilarity}
                            onChange={(e) => setMinSimilarity(parseFloat(e.target.value))}
                            style={{
                                padding: "4px 8px",
                                background: "#0d1117",
                                color: "#c9d1d9",
                                border: "1px solid #30363d",
                                borderRadius: 4,
                            }}
                        >
                            <option value={0.45}>Medium (45%)</option>
                            <option value={0.6}>Med-high (60%)</option>
                            <option value={0.7}>High (70%)</option>
                            <option value={0.85}>Very high (85%)</option>
                        </select>
                        <button
                            onClick={() => fetchSuggestions(minSimilarity)}
                            disabled={loadingSuggestions}
                            style={{
                                background: "#21262d",
                                color: "#c9d1d9",
                                border: "1px solid #30363d",
                                borderRadius: 4,
                                padding: "4px 10px",
                                fontSize: 12,
                                cursor: loadingSuggestions ? "wait" : "pointer",
                            }}
                        >
                            <RefreshCw size={12} style={{ marginRight: 4, verticalAlign: "middle" }} />
                            Refresh suggestions
                        </button>
                        {loadingSuggestions && (
                            <button
                                onClick={() => autoSuggestAbortRef.current?.abort()}
                                style={{
                                    background: "#3b1d1d",
                                    color: "#fca5a5",
                                    border: "1px solid #7f1d1d",
                                    borderRadius: 4,
                                    padding: "4px 10px",
                                    fontSize: 12,
                                    cursor: "pointer",
                                }}
                            >
                                Cancel
                            </button>
                        )}
                        <span style={{ fontSize: 12, color: "#8b949e", marginLeft: "auto" }}>
                            {loadingSuggestions ? `Loading... ${suggestionsElapsedSec}s` : `${suggestions.length} suggestions`}
                        </span>
                    </>
                )}
            </div>

            {mode === "auto" && !loadingSuggestions && (
                <div style={{ fontSize: 12, color: "#8b949e", marginBottom: 10 }}>
                    Cache rows: {cacheRows} • Last refreshed: {cacheLastRefreshedAt ? new Date(cacheLastRefreshedAt).toLocaleString() : "not yet populated"}
                </div>
            )}

            {mode === "auto" && !loadingSuggestions && jobStatus && (
                <div
                    style={{
                        fontSize: 12,
                        marginBottom: 10,
                        padding: "8px 10px",
                        borderRadius: 6,
                        border: "1px solid",
                        borderColor: jobStatus.LastStatus === 'Failed' ? "#f85149" : (jobStatus.LastStatus === 'Success' ? "#2ea043" : "#30363d"),
                        background: jobStatus.LastStatus === 'Failed' ? "#3b1418" : "#0d1117",
                        color: jobStatus.LastStatus === 'Failed' ? "#f85149" : "#8b949e",
                    }}
                >
                    Scheduled job: <strong>XeroLinkAutoSuggestCacheRefresh</strong>{" "}
                    — {jobStatus.Enabled === false ? "Disabled" : "Enabled"}
                    {jobStatus.LastRunAt ? ` • Last run ${new Date(jobStatus.LastRunAt).toLocaleString()}` : " • Never run"}
                    {jobStatus.LastStatus ? ` • Status: ${jobStatus.LastStatus}` : ""}
                    {jobStatus.LastStatus === 'Failed' && jobStatus.LastError ? (
                        <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>Error: {jobStatus.LastError}</div>
                    ) : null}
                    {cacheRows === 0 && (
                        <div style={{ marginTop: 4 }}>Cache is empty — visit /scheduled-jobs to run this job manually.</div>
                    )}
                </div>
            )}

            {mode === "auto" ? (
                <div
                    style={{
                        background: "#0d1117",
                        border: "1px solid #30363d",
                        borderRadius: 6,
                        padding: 12,
                    }}
                >
                    {loadingSuggestions ? (
                        <div style={{ color: "#8b949e", padding: 24, textAlign: "center" }}>
                            <div style={{ marginBottom: 10 }}>{suggestionsStatus || "Loading suggestions..."}</div>
                            <div style={{ height: 6, background: "#1f2937", borderRadius: 999, overflow: "hidden", maxWidth: 360, margin: "0 auto" }}>
                                <div
                                    style={{
                                        width: "35%",
                                        height: "100%",
                                        background: "linear-gradient(90deg, #2563eb, #60a5fa)",
                                        animation: "xeroLinkerIndeterminate 1.2s ease-in-out infinite"
                                    }}
                                />
                            </div>
                            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.9 }}>Elapsed: {suggestionsElapsedSec}s</div>
                        </div>
                    ) : suggestions.length === 0 ? (
                        <div style={{ color: "#8b949e", padding: 24, textAlign: "center" }}>
                            No suggestions at or above this similarity threshold.
                            {cacheRows === 0 && (
                                <div style={{ marginTop: 8, fontSize: 12 }}>
                                    The precomputed cache is empty. Run the scheduled job <strong>XeroLinkAutoSuggestCacheRefresh</strong> in Scheduled Jobs, then refresh suggestions.
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {suggestions.map((s) => {
                                const pill = confidencePill(
                                    s.name_similarity,
                                    s.client_ref_match,
                                    s.email_match,
                                    s.email_domain_match
                                );
                                const alreadyLinked =
                                    s.existing_senta_company_id || s.existing_business_id;
                                return (
                                    <div
                                        key={s.business_id}
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "1.2fr 1.2fr 160px 140px",
                                            gap: 12,
                                            alignItems: "center",
                                            padding: 10,
                                            border: "1px solid #30363d",
                                            borderRadius: 4,
                                            background: "#161b22",
                                        }}
                                    >
                                        <div>
                                            <div style={{ fontWeight: 500 }}>
                                                {s.business_name || s.senta_title || "(unnamed)"}
                                            </div>
                                            {s.senta_title && s.business_name && s.senta_title !== s.business_name && (
                                                <div style={{ fontSize: 11, color: "#8b949e" }}>
                                                    Senta: {s.senta_title}
                                                </div>
                                            )}
                                            <div style={{ fontSize: 11, color: "#8b949e" }}>
                                                {s.client_ref ? <>ref <code>{s.client_ref}</code> • </> : null}
                                                {s.client_state ? CLIENT_STATE_LABEL[s.client_state] || s.client_state : "—"}
                                            </div>
                                            {s.primary_contact_email && (
                                                <div style={{ fontSize: 11, color: "#58a6ff" }}>
                                                    {s.primary_contact_email}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 500 }}>{s.candidate_name || "(unnamed)"}</div>
                                            <div style={{ fontSize: 11, color: "#8b949e" }}>
                                                <span
                                                    style={{
                                                        color: s.email_match
                                                            ? "#2ea043"
                                                            : s.email_domain_match
                                                            ? "#d29922"
                                                            : "#8b949e",
                                                        fontWeight: s.email_match ? 600 : 400,
                                                    }}
                                                >
                                                    {s.email_match ? "✓ " : s.email_domain_match ? "~ " : ""}
                                                    {s.candidate_email || "no email"}
                                                </span>
                                                {s.candidate_client_ref ? ` • ref ${s.candidate_client_ref}` : ""}
                                                {!s.candidate_active ? " • inactive" : ""}
                                            </div>
                                            {alreadyLinked ? (
                                                <div style={{ fontSize: 11, color: "#d29922", marginTop: 2 }}>
                                                    ⚠ already linked to{" "}
                                                    <strong>
                                                        {s.existing_business_name || s.existing_senta_title || (s.existing_business_id ? `business #${s.existing_business_id}` : `senta #${s.existing_senta_company_id}`)}
                                                    </strong>
                                                    {s.existing_client_ref ? ` (ref ${s.existing_client_ref})` : ""}
                                                </div>
                                            ) : null}
                                        </div>
                                        <div style={{ textAlign: "center" }}>
                                            <span
                                                style={{
                                                    fontSize: 10,
                                                    padding: "2px 6px",
                                                    background: pill.color,
                                                    color: "#fff",
                                                    borderRadius: 10,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {pill.label}
                                            </span>
                                            <div style={{ fontSize: 11, color: "#8b949e", marginTop: 4 }}>
                                                {(s.name_similarity * 100).toFixed(0)}% name
                                            </div>
                                        </div>
                                        <div style={{ textAlign: "right" }}>
                                            <button
                                                disabled={linkingId === s.candidate_id}
                                                onClick={() =>
                                                    linkContact(
                                                        {
                                                            business_id: s.business_id,
                                                            senta_company_id: s.senta_company_id,
                                                            business_name: s.business_name,
                                                            senta_title: s.senta_title,
                                                        },
                                                        s.candidate_id
                                                    )
                                                }
                                                style={{
                                                    background: "#238636",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: 4,
                                                    padding: "6px 12px",
                                                    fontSize: 12,
                                                    cursor: linkingId === s.candidate_id ? "wait" : "pointer",
                                                    opacity: linkingId === s.candidate_id ? 0.6 : 1,
                                                }}
                                            >
                                                {linkingId === s.candidate_id ? "Linking…" : "Confirm link"}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 16 }}>
                <div>
                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                        <input
                            type="text"
                            placeholder="Filter by name, ref, or senta id…"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            style={{
                                flex: 1,
                                padding: "6px 10px",
                                background: "#0d1117",
                                color: "#c9d1d9",
                                border: "1px solid #30363d",
                                borderRadius: 4,
                            }}
                        />
                        <select
                            value={stateFilter}
                            onChange={(e) => setStateFilter(e.target.value)}
                            style={{
                                padding: "6px 10px",
                                background: "#0d1117",
                                color: "#c9d1d9",
                                border: "1px solid #30363d",
                                borderRadius: 4,
                                minWidth: 160,
                            }}
                        >
                            <option value="__all__">All states ({businesses.length})</option>
                            {Object.entries(CLIENT_STATE_LABEL).map(([k, label]) => (
                                <option key={k} value={k}>
                                    {label} ({stateCounts[k] || 0})
                                </option>
                            ))}
                            {Object.keys(stateCounts)
                                .filter((k) => k !== "__none__" && !(k in CLIENT_STATE_LABEL))
                                .map((k) => (
                                    <option key={k} value={k}>
                                        {k} ({stateCounts[k]})
                                    </option>
                                ))}
                            {stateCounts["__none__"] ? (
                                <option value="__none__">(none) ({stateCounts["__none__"]})</option>
                            ) : null}
                        </select>
                    </div>
                    <div style={{ fontSize: 12, color: "#8b949e", marginBottom: 6 }}>
                        {filtered.length} of {businesses.length} unlinked businesses
                    </div>
                    <DataTable
                        columns={columns}
                        data={filtered}
                        pagination
                        paginationPerPage={25}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        dense
                        highlightOnHover
                        theme="dark"
                        noDataComponent={
                            <div style={{ padding: 24, color: "#8b949e" }}>
                                No unlinked businesses 🎉
                            </div>
                        }
                    />
                </div>

                <div
                    style={{
                        background: "#0d1117",
                        border: "1px solid #30363d",
                        borderRadius: 6,
                        padding: 16,
                        position: "sticky",
                        top: 16,
                        alignSelf: "start",
                        maxHeight: "85vh",
                        overflowY: "auto",
                    }}
                >
                    {!selected ? (
                        <div style={{ color: "#8b949e", textAlign: "center", padding: 32 }}>
                            Select a business to see suggested Xero contact matches.
                        </div>
                    ) : (
                        <>
                            <h3 style={{ marginTop: 0 }}>
                                {selected.business_name || selected.senta_title}
                            </h3>
                            <div style={{ fontSize: 12, color: "#8b949e", marginBottom: 4 }}>
                                Business #{selected.business_id} • Senta company #
                                {selected.senta_company_id} •{" "}
                                {selected.client_ref ? (
                                    <>
                                        ref <code>{selected.client_ref}</code>
                                    </>
                                ) : (
                                    "no client_ref"
                                )}
                            </div>
                            <div style={{ fontSize: 12, color: "#58a6ff", marginBottom: 12 }}>
                                {selected.primary_contact_email
                                    ? <>Primary contact: <code>{selected.primary_contact_email}</code></>
                                    : <span style={{ color: "#8b949e" }}>No primary contact email on file</span>}
                            </div>

                            {loadingCandidates ? (
                                <div style={{ color: "#8b949e" }}>Searching…</div>
                            ) : candidates.length === 0 ? (
                                <div style={{ color: "#8b949e" }}>No candidate Xero contacts found.</div>
                            ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                    {candidates.map((c) => {
                                        const pill = confidencePill(
                                            c.name_similarity,
                                            c.client_ref_match,
                                            c.email_match,
                                            c.email_domain_match
                                        );
                                        const alreadyLinked =
                                            c.existing_senta_company_id || c.existing_business_id;
                                        return (
                                            <div
                                                key={c.id}
                                                style={{
                                                    padding: 10,
                                                    border: "1px solid #30363d",
                                                    borderRadius: 4,
                                                    background: "#161b22",
                                                }}
                                            >
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: 500 }}>{c.name}</div>
                                                        <div style={{ fontSize: 11, color: "#8b949e" }}>
                                                            <span
                                                                style={{
                                                                    color: c.email_match
                                                                        ? "#2ea043"
                                                                        : c.email_domain_match
                                                                        ? "#d29922"
                                                                        : "#8b949e",
                                                                    fontWeight: c.email_match ? 600 : 400,
                                                                }}
                                                            >
                                                                {c.email_match ? "✓ " : c.email_domain_match ? "~ " : ""}
                                                                {c.email || "no email"}
                                                            </span>
                                                            {c.client_ref ? ` • ref ${c.client_ref}` : ""}
                                                            {!c.contact_active ? " • inactive" : ""}
                                                        </div>
                                                        {alreadyLinked ? (
                                                            <div style={{ fontSize: 11, color: "#d29922", marginTop: 4 }}>
                                                                ⚠ already linked to{" "}
                                                                <strong>
                                                                    {c.existing_business_name || c.existing_senta_title || (c.existing_business_id ? `business #${c.existing_business_id}` : `senta #${c.existing_senta_company_id}`)}
                                                                </strong>
                                                                {c.existing_client_ref ? ` (ref ${c.existing_client_ref})` : ""}
                                                                ; relinking will overwrite.
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                    <div style={{ textAlign: "right" }}>
                                                        <span
                                                            style={{
                                                                fontSize: 10,
                                                                padding: "2px 6px",
                                                                background: pill.color,
                                                                color: "#fff",
                                                                borderRadius: 10,
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {pill.label}
                                                        </span>
                                                        <div style={{ fontSize: 11, color: "#8b949e", marginTop: 4 }}>
                                                            {(c.name_similarity * 100).toFixed(0)}%
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: 8, textAlign: "right" }}>
                                                    <button
                                                        disabled={linkingId === c.id}
                                                        onClick={() => selected && linkContact(selected, c.id)}
                                                        style={{
                                                            background: "#238636",
                                                            color: "#fff",
                                                            border: "none",
                                                            borderRadius: 4,
                                                            padding: "4px 10px",
                                                            fontSize: 12,
                                                            cursor: linkingId === c.id ? "wait" : "pointer",
                                                            opacity: linkingId === c.id ? 0.6 : 1,
                                                        }}
                                                    >
                                                        {linkingId === c.id ? "Linking…" : "Link this contact"}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            )}
            <style>{`
                @keyframes xeroLinkerIndeterminate {
                    0% { transform: translateX(-130%); }
                    100% { transform: translateX(320%); }
                }
            `}</style>
        </div>
    );
};

export default XeroContactLinker;
